import { useCallback, useEffect, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { useNavigate } from "react-router-dom";
import { adminService } from "../../features/admin/service";
import { dealsService } from "../../features/deals/service";
import { geoService } from "../../features/geo/service";
import { useCountries } from "../../features/geo/hooks";
import { CategorySelect } from "../../features/deals/CategorySelect";
import type {
  PaginatedResponse,
  CommunityAudiencePreview,
  CommunityEngagement,
  CommunityLikeActor,
  CommunityResponseDetail,
  CommunityCommentDetail,
} from "../../features/admin/types";
import { formatIST } from "../../utils/dateUtils";
import { useAdminToast } from "../../context/AdminToastContext";
import ConfirmModal from "../../components/admin/ConfirmModal";
import Pagination from "../../components/admin/Pagination";
import { useAlertModal } from "../../components/admin/AlertModal";
import Spinner from "../../components/ui/Spinner";
import RefreshButton from "../../components/admin/RefreshButton";

const TYPES = ["all", "poll", "question", "announcement"];

interface FilterOptions {
  countries: string[];
  cities: string[];
  tags: string[];
}

const CHIP_COLORS: Record<"country" | "city" | "tag", string> = {
  country: "bg-sky-500/20 text-sky-300 border-sky-500/30",
  city: "bg-violet-500/20 text-violet-300 border-violet-500/30",
  tag: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
};

const ChipSection: React.FC<{
  type: "country" | "city" | "tag";
  label: string;
  icon: string;
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
}> = ({ type, label, icon, options, selected, onToggle }) => {
  const [search, setSearch] = useState("");
  const q = search.toLowerCase().trim();
  // Selected values render as removable pills above, so drop them from the
  // pickable options list below to avoid duplication.
  const pickable = options.filter((o) => !selected.includes(o));
  const visible = q ? pickable.filter((o) => o.toLowerCase().includes(q)) : pickable;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-300 flex items-center gap-1.5">
          <span>{icon}</span>
          {label}
          {selected.length > 0 && (
            <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
              {selected.length}
            </span>
          )}
        </label>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search…`}
          className="px-2 py-1 text-xs rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 w-32"
        />
      </div>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((value) => (
            <span
              key={value}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${CHIP_COLORS[type]}`}
            >
              {value}
              <button
                type="button"
                onClick={() => onToggle(value)}
                aria-label={`Remove ${value}`}
                className="opacity-70 hover:opacity-100 transition-opacity"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-1 rounded-lg bg-gray-800/40 border border-gray-800">
        {visible.length === 0 ? (
          <span className="text-xs text-gray-500 p-1">No options</span>
        ) : (
          visible.map((value) => {
            const active = selected.includes(value);
            return (
              <button
                key={value}
                type="button"
                onClick={() => onToggle(value)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                  active
                    ? `${CHIP_COLORS[type]} shadow-sm`
                    : "bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-600"
                }`}
              >
                {value}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

const EMPTY_FORM = {
  post_type: "poll" as "poll" | "question" | "announcement",
  title: "",
  body: "",
  category: "",
  tags: [] as string[],
  media_urls: [] as string[],
  target_roles: [] as string[],
  target_countries: [] as string[],
  target_cities: [] as string[],
  target_tags: [] as string[],
  mandatory_response: false,
  send_fcm: false,
};

const RoleBadge: React.FC<{ role?: string }> = ({ role }) => (
  <span
    className={`px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${
      role === "seller"
        ? "bg-emerald-500/15 text-emerald-300"
        : role === "super_admin"
        ? "bg-amber-500/15 text-amber-300"
        : "bg-sky-500/15 text-sky-300"
    }`}
  >
    {role || "buyer"}
  </span>
);

const ActorRow: React.FC<{
  name: string;
  role?: string;
  at?: string | null;
  children?: React.ReactNode;
}> = ({ name, role, at, children }) => (
  <div className="flex items-start gap-2 py-1.5 border-b border-gray-800/40 last:border-0">
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-gray-200 font-medium">{name}</span>
        <RoleBadge role={role} />
      </div>
      {children}
    </div>
    <span className="text-[11px] text-gray-500 flex-shrink-0 whitespace-nowrap">
      {at ? formatIST(at) : "—"}
    </span>
  </div>
);

const EngSection: React.FC<{ title: string; count: number; children: React.ReactNode }> = ({
  title,
  count,
  children,
}) => (
  <div className="space-y-1">
    <p className="text-xs uppercase tracking-wide text-gray-500">
      {title} <span className="text-gray-600">({count})</span>
    </p>
    {count === 0 ? (
      <p className="text-xs text-gray-600 italic py-1">None yet</p>
    ) : (
      <div className="rounded-lg bg-gray-800/30 border border-gray-800 px-3">{children}</div>
    )}
  </div>
);

const EngagementPanel: React.FC<{
  engagement?: CommunityEngagement;
  loading: boolean;
  postType: string;
}> = ({ engagement, loading, postType }) => {
  if (loading && !engagement) {
    return <p className="text-xs text-gray-500 py-2">Loading engagement…</p>;
  }
  if (!engagement) {
    return <p className="text-xs text-gray-600 py-2">No engagement data.</p>;
  }
  const { votes, answers, acknowledgements, comments, post_likes } = engagement;
  // Tracked answers are also stored as comments; drop those from the generic
  // comment list so each answer shows once (under "Answers" for questions).
  const plainComments = comments.filter(
    (c: CommunityCommentDetail) =>
      !answers.some((a) => a.user_id === c.author_id && (a.answer_body || "") === c.body),
  );

  return (
    <div className="space-y-4 pt-3">
      <p className="text-xs uppercase tracking-wide text-gray-500">Engagement detail · who & when</p>

      {postType === "poll" && (
        <EngSection title="Votes" count={votes.length}>
          {votes.map((v: CommunityResponseDetail) => (
            <ActorRow key={v.id} name={v.user_name} role={v.user_role} at={v.created_at}>
              <p className="text-xs text-violet-300 mt-0.5">
                Voted: {v.option_text || `Option ${(v.option_index ?? 0) + 1}`}
              </p>
            </ActorRow>
          ))}
        </EngSection>
      )}

      {postType === "question" && (
        <EngSection title="Answers" count={answers.length}>
          {answers.map((a: CommunityResponseDetail) => (
            <ActorRow key={a.id} name={a.user_name} role={a.user_role} at={a.created_at}>
              <p className="text-sm text-gray-300 mt-0.5 whitespace-pre-wrap">{a.answer_body}</p>
            </ActorRow>
          ))}
        </EngSection>
      )}

      {postType === "announcement" && (
        <EngSection title="Acknowledgements" count={acknowledgements.length}>
          {acknowledgements.map((a: CommunityResponseDetail) => (
            <ActorRow key={a.id} name={a.user_name} role={a.user_role} at={a.created_at} />
          ))}
        </EngSection>
      )}

      <EngSection title="Comments" count={plainComments.length}>
        {plainComments.map((c: CommunityCommentDetail) => (
          <ActorRow key={c.id} name={c.author_name} role={c.author_role} at={c.created_at}>
            <p className="text-sm text-gray-300 mt-0.5 whitespace-pre-wrap">{c.body}</p>
            <div className="flex items-center gap-2 mt-1">
              {c.is_accepted && (
                <span className="text-[10px] text-emerald-400">✓ accepted answer</span>
              )}
              <span className="text-[11px] text-gray-500">{c.like_count} likes</span>
              {c.liked_by.length > 0 && (
                <span className="text-[11px] text-gray-500">
                  · liked by {c.liked_by.map((l) => l.user_name).join(", ")}
                </span>
              )}
            </div>
          </ActorRow>
        ))}
      </EngSection>

      <EngSection title="Post likes" count={post_likes.length}>
        {post_likes.map((l: CommunityLikeActor) => (
          <ActorRow key={l.user_id} name={l.user_name} role={l.user_role} at={l.created_at} />
        ))}
      </EngSection>
    </div>
  );
};

const AdminPollsPage: React.FC = () => {
  const [data, setData] = useState<PaginatedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { alertModal, showAlert } = useAlertModal();
  const { showError } = useAdminToast();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [pollOptions, setPollOptions] = useState<{ text: string; votes?: number }[]>([{ text: "" }, { text: "" }]);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({ countries: [], cities: [], tags: [] });
  // Countries / cities for targeting come from the geo API — the same source &
  // values (plain names, no emoji) onboarding stores on the user record, so
  // targeting actually matches. Tags still come from deal filter-options.
  const { countries: geoCountries } = useCountries();
  const [cityOptions, setCityOptions] = useState<string[]>([]);
  const [selectedMediaFile, setSelectedMediaFile] = useState<File | null>(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [uploadedMediaUrl, setUploadedMediaUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  useEffect(() => { if (formError) showError(formError); }, [formError, showError]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [engagement, setEngagement] = useState<Record<string, CommunityEngagement>>({});
  const [engagementLoading, setEngagementLoading] = useState<string | null>(null);
  const [audiencePreview, setAudiencePreview] = useState<CommunityAudiencePreview | null>(null);
  const [audienceLoading, setAudienceLoading] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [statusBusyId, setStatusBusyId] = useState<string | null>(null);

  const navigate = useNavigate();

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, page_size: pageSize };
      if (search) params.search = search;
      if (typeFilter !== "all") params.post_type = typeFilter;
      setData(await adminService.getCommunityPosts(params));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, typeFilter]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);
  useEffect(() => { setPage(1); }, [pageSize]);

  useEffect(() => {
    dealsService.getFilterOptions()
      .then((opts) => setFilterOptions({
        countries: opts.countries || [],
        cities: opts.cities || [],
        tags: opts.tags || [],
      }))
      .catch(() => {});
  }, []);

  // Cascade cities from the selected target countries (geo source), mirroring
  // onboarding's destinations step. City chips stay empty until a country is
  // chosen.
  useEffect(() => {
    let cancelled = false;
    const selected = form.target_countries;
    if (!selected.length || !geoCountries.length) { setCityOptions([]); return; }
    const isoByName = new Map(geoCountries.map((c) => [c.name.toLowerCase(), c.iso2]));
    Promise.all(
      selected.map((name) => {
        const iso = isoByName.get(name.toLowerCase());
        return iso ? geoService.getCities(iso).catch(() => []) : Promise.resolve([]);
      }),
    ).then((lists) => {
      if (cancelled) return;
      const names = new Set<string>();
      lists.forEach((list) => list.forEach((c) => names.add(c.name)));
      setCityOptions(Array.from(names).sort());
    });
    return () => { cancelled = true; };
  }, [form.target_countries, geoCountries]);

  const toggleExpand = useCallback(
    async (postId: string) => {
      const next = expandedId === postId ? null : postId;
      setExpandedId(next);
      if (next && !engagement[next]) {
        setEngagementLoading(next);
        try {
          const detail = await adminService.getCommunityEngagement(next);
          setEngagement((cur) => ({ ...cur, [next]: detail }));
        } catch (err) {
          console.error(err);
        } finally {
          setEngagementLoading((cur) => (cur === next ? null : cur));
        }
      }
    },
    [expandedId, engagement],
  );

  const handleDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await adminService.deleteCommunityPost(deleteTarget.id);
      setDeleteTarget(null);
      fetchPosts();
    } catch (err: any) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      showAlert(typeof detail === "string" ? detail : "Failed to delete post.");
    } finally {
      setDeleting(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setPollOptions([{ text: "" }, { text: "" }]);
    setAudiencePreview(null);
    setSelectedMediaFile(null);
    setUploadedMediaUrl(null);
    setFormError("");
    setShowForm(true);
  };

  const openEdit = (post: any) => {
    setEditingId(post.id);
    setForm({
      ...EMPTY_FORM,
      post_type: post.post_type,
      title: post.title || "",
      body: post.body || "",
      category: post.category || "",
      tags: post.tags || [],
      media_urls: post.media_urls || [],
      target_roles: post.target_roles || [],
      target_countries: post.target_countries || [],
      target_cities: post.target_cities || [],
      target_tags: post.target_tags || [],
      mandatory_response: !!post.mandatory_response,
      send_fcm: false,
    });
    setPollOptions(
      post.post_type === "poll" && post.poll_options?.length
        ? post.poll_options.map((o: any) => ({ text: o.text || "", votes: o.votes || 0 }))
        : [{ text: "" }, { text: "" }],
    );
    setUploadedMediaUrl(post.media_urls?.[0] || null);
    setSelectedMediaFile(null);
    setAudiencePreview(null);
    setFormError("");
    setShowForm(true);
  };

  const toggleStatus = async (post: any) => {
    const nextStatus = post.status === "closed" ? "open" : "closed";
    setStatusBusyId(post.id);
    try {
      await adminService.updateCommunityPost(post.id, { status: nextStatus });
      fetchPosts();
    } catch (err) {
      console.error(err);
    } finally {
      setStatusBusyId(null);
    }
  };

  const handleAudiencePreview = async () => {
    setAudiencePreview(null);
    setAudienceLoading(true);
    try {
      const result = await adminService.previewCommunityAudience({
        target_roles: form.target_roles,
        target_countries: form.target_countries,
        target_cities: form.target_cities,
        target_tags: form.target_tags,
      });
      setAudiencePreview(result);
    } catch (err) {
      console.error(err);
      setAudiencePreview(null);
    } finally {
      setAudienceLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) { setFormError("Title is required"); return; }
    if (form.post_type === "poll") {
      const filled = pollOptions.filter((o) => o.text.trim());
      if (filled.length < 2) { setFormError("At least 2 poll options required"); return; }
    }
    setSubmitting(true);
    setFormError("");
    try {
      const mediaUrls = [...form.media_urls];
      if (form.post_type === "announcement" && selectedMediaFile && mediaUrls.length === 0) {
        setUploadingMedia(true);
        try {
          const uploadResult = await adminService.uploadCommunityMedia(selectedMediaFile);
          mediaUrls.push(uploadResult.url);
          setUploadedMediaUrl(uploadResult.url);
          setForm((current) => ({ ...current, media_urls: [uploadResult.url] }));
        } catch (uploadErr: any) {
          throw new Error(uploadErr?.response?.data?.detail || "Media upload failed");
        } finally {
          setUploadingMedia(false);
        }
      }

      const payload: Record<string, any> = {
        post_type: form.post_type,
        title: form.title.trim(),
        body: form.body.trim(),
        tags: form.tags,
        destinations: [],
        media_urls: mediaUrls,
        target_roles: form.target_roles,
        target_countries: form.target_countries,
        target_cities: form.target_cities,
        target_tags: form.target_tags,
        mandatory_response: form.mandatory_response,
      };
      if (form.category.trim()) payload.category = form.category.trim();
      if (form.post_type === "poll") {
        // Preserve existing vote counts by option position; new options default to 0.
        payload.poll_options = pollOptions.filter((o) => o.text.trim()).map((o) => ({ text: o.text.trim(), votes: o.votes || 0 }));
      }

      if (editingId) {
        await adminService.updateCommunityPost(editingId, payload);
        setShowForm(false);
        setEditingId(null);
        setForm({ ...EMPTY_FORM });
        setPollOptions([{ text: "" }, { text: "" }]);
        setAudiencePreview(null);
        fetchPosts();
        return;
      }

      const created = await adminService.createCommunityPost(payload);
      setShowForm(false);
      setForm({ ...EMPTY_FORM });
      setPollOptions([{ text: "" }, { text: "" }]);
      setAudiencePreview(null);
      fetchPosts();
      if (form.send_fcm) {
        const postId = created.id || created._id || "";
        const params = new URLSearchParams();
        params.set("source", "community");
        params.set("title", form.title.trim());
        params.set("body", form.body.trim() || "New community update");
        params.set("deep_link", `/community/posts/${postId}`);
        if (form.target_roles.length) params.set("role", form.target_roles.join(","));
        if (payload.target_countries.length) params.set("countries", payload.target_countries.join(","));
        if (payload.target_cities.length) params.set("cities", payload.target_cities.join(","));
        if (payload.target_tags.length) params.set("tags", payload.target_tags.join(","));
        navigate(`/admin/channels/notifications?${params.toString()}`);
      }
    } catch (err: any) {
      setFormError(err?.response?.data?.detail || "Failed to create post");
    } finally {
      setSubmitting(false);
    }
  };

  const typeColor = (t: string) => {
    const map: Record<string, string> = {
      poll: "bg-violet-500/10 text-violet-400",
      question: "bg-amber-500/10 text-amber-400",
      discussion: "bg-indigo-500/10 text-indigo-400",
      announcement: "bg-red-500/10 text-red-400",
      media_share: "bg-cyan-500/10 text-cyan-400",
    };
    return map[t] || "bg-gray-500/10 text-gray-400";
  };

  const totalVotes = (options: any[]) => (options || []).reduce((s, o) => s + (o.votes || 0), 0);
  const previewMediaUrl = form.media_urls[0] ?? "";
  // Strip query/hash so extension detection works on presigned S3 URLs
  // (e.g. ".../photo.jpg?X-Amz-Signature=...").
  const previewMediaPath = previewMediaUrl.split(/[?#]/)[0];
  const isPreviewImage = /\.(jpe?g|png|webp|gif)$/i.test(previewMediaPath);
  const isPreviewVideo = /\.(mp4|webm)$/i.test(previewMediaPath);

  return (
    <>
      <div>
        <AdminPageHeader
          title="Polls & Community"
          subtitle="Create polls, QnA & community posts"
          meta={`${data?.total ?? 0} posts`}
          actions={
            <>
              <RefreshButton onRefresh={fetchPosts} />
              <button
                onClick={openCreate}
                className="px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white rounded-xl text-sm font-medium transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Post
              </button>
            </>
          }
        />

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <input
            type="text"
            placeholder="Search posts..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="px-4 py-2.5 bg-gray-900 border border-gray-800 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 w-72"
          />
          <div className="flex gap-2 flex-wrap">
            {TYPES.map((t) => (
              <button
                key={t}
                onClick={() => { setTypeFilter(t); setPage(1); }}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  typeFilter === t ? "bg-violet-500/20 text-violet-400 border border-violet-500/30" : "bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800"
                }`}
              >
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Cards grid for polls, table for others */}
        {loading ? (
          <div className="text-center py-20 text-gray-500"><Spinner /></div>
        ) : data?.items.length === 0 ? (
          <div className="text-center py-20 text-gray-500">No posts found</div>
        ) : (
          <div className="space-y-3">
            {data?.items.map((post) => (
              <div key={post.id} className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                <div
                  className="flex items-start gap-4 p-4 cursor-pointer hover:bg-gray-800/30 transition-colors"
                  onClick={() => toggleExpand(post.id)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${typeColor(post.post_type)}`}>
                        {post.post_type}
                      </span>
                      {post.post_type === "poll" && (
                        <span className="text-xs text-gray-500">
                          {totalVotes(post.poll_options)} votes
                        </span>
                      )}
                      <span
                        className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                          post.status === "closed"
                            ? "bg-gray-500/10 text-gray-400"
                            : "bg-emerald-500/10 text-emerald-400"
                        }`}
                      >
                        {post.status === "closed" ? "Inactive" : "Active"}
                      </span>
                    </div>
                    <p className="text-white font-medium truncate">{post.title}</p>
                    <p className="text-gray-500 text-xs mt-0.5">
                      {post.author_name || "Admin"} · {post.created_at ? formatIST(post.created_at) : "—"} · {post.comment_count ?? 0} replies · {post.like_count ?? 0} likes
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleExpand(post.id); }}
                      className="px-2 py-1 text-xs text-indigo-300 hover:bg-indigo-500/10 rounded-md transition-colors"
                    >
                      View
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleStatus(post); }}
                      disabled={statusBusyId === post.id}
                      className={`px-2 py-1 text-xs rounded-md transition-colors disabled:opacity-50 ${
                        post.status === "closed"
                          ? "text-emerald-300 hover:bg-emerald-500/10"
                          : "text-amber-300 hover:bg-amber-500/10"
                      }`}
                    >
                      {statusBusyId === post.id
                        ? "…"
                        : post.status === "closed"
                        ? "Activate"
                        : "Deactivate"}
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget(post); }}
                      className="px-2 py-1 text-xs text-red-400 hover:bg-red-500/10 rounded-md transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {/* Expanded: poll results */}
                {expandedId === post.id && post.post_type === "poll" && post.poll_options?.length > 0 && (
                  <div className="px-4 pb-4 pt-1 border-t border-gray-800/60">
                    <p className="text-xs text-gray-500 mb-3 uppercase tracking-wide">Poll Results</p>
                    <div className="space-y-2.5">
                      {post.poll_options.map((opt: any, i: number) => {
                        const tv = totalVotes(post.poll_options);
                        const pct = tv > 0 ? Math.round((opt.votes / tv) * 100) : 0;
                        return (
                          <div key={i}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-gray-300">{opt.text}</span>
                              <span className="text-gray-500">{opt.votes} ({pct}%)</span>
                            </div>
                            <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-violet-500 rounded-full transition-all"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Expanded: question/discussion body */}
                {expandedId === post.id && post.post_type !== "poll" && post.body && (
                  <div className="px-4 pb-2 pt-1 border-t border-gray-800/60">
                    <p className="text-gray-400 text-sm whitespace-pre-wrap">{post.body}</p>
                  </div>
                )}

                {/* Expanded: full engagement detail (who voted/answered/acknowledged/commented/liked & when) */}
                {expandedId === post.id && (
                  <div className="px-4 pb-4 border-t border-gray-800/60">
                    <div className="flex justify-end pt-3 pb-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); openEdit(post); }}
                        className="px-3 py-1.5 text-xs font-medium text-indigo-400 bg-indigo-500/10 hover:bg-indigo-500/20 rounded-lg transition-colors"
                      >
                        Edit
                      </button>
                    </div>
                    <EngagementPanel
                      engagement={engagement[post.id]}
                      loading={engagementLoading === post.id}
                      postType={post.post_type}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

      </div>

      {data && data.total > 0 && (
        <Pagination
          page={page - 1}
          pageSize={pageSize}
          total={data.total}
          onPageChange={(p) => setPage(p + 1)}
          onPageSizeChange={setPageSize}
        />
      )}

      {/* Create Poll / Post Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-7xl max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-800">
              <h2 className="text-lg font-semibold text-white">{editingId ? "Edit Post" : "Create Post"}</h2>
              <button onClick={() => { setShowForm(false); setEditingId(null); }} className="text-gray-400 hover:text-white transition-colors">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
              <div className="flex-1 min-w-0 space-y-4">
              {formError && <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2">{formError}</p>}

              {/* Type selector — admin post types */}
              <div className="flex gap-2 p-1 bg-gray-800 rounded-xl flex-wrap">
                {([
                  { key: "poll", label: "Poll" },
                  { key: "question", label: "Q&A" },
                  { key: "announcement", label: "Announcement" },
                ] as const).map((typeOption) => (
                  <button
                    key={typeOption.key}
                    type="button"
                    onClick={() => {
                      const nextType = typeOption.key;
                      const nextForm = { ...form, post_type: nextType };
                      if (nextType !== "announcement") {
                        nextForm.tags = [];
                        nextForm.media_urls = [];
                        setSelectedMediaFile(null);
                        setUploadedMediaUrl(null);
                      }
                      setForm(nextForm);
                    }}
                    className={`flex-1 min-w-[90px] py-2 rounded-lg text-xs font-medium transition-colors ${
                      form.post_type === typeOption.key ? "bg-gray-700 text-white shadow" : "text-gray-400 hover:text-gray-300"
                    }`}
                  >
                    {typeOption.label}
                  </button>
                ))}
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1.5">
                  {form.post_type === "poll" ? "Poll Question" : form.post_type === "question" ? "Your Question" : "Title"}{" "}
                  <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder={
                    form.post_type === "poll" ? "e.g. What's the best time to visit Maldives?"
                    : form.post_type === "question" ? "e.g. How do I get a visa for Japan?"
                    : "Title..."
                  }
                  className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                />
              </div>

              {/* Poll options */}
              {form.post_type === "poll" && (
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Options</label>
                  <div className="space-y-2">
                    {pollOptions.map((opt, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center text-xs text-gray-400 flex-shrink-0">
                          {String.fromCharCode(65 + i)}
                        </div>
                        <input
                          type="text"
                          value={opt.text}
                          onChange={(e) => {
                            const updated = [...pollOptions];
                            updated[i] = { ...updated[i], text: e.target.value };
                            setPollOptions(updated);
                          }}
                          placeholder={`Option ${String.fromCharCode(65 + i)}`}
                          className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 text-sm"
                        />
                        {pollOptions.length > 2 && (
                          <button
                            type="button"
                            onClick={() => setPollOptions(pollOptions.filter((_, j) => j !== i))}
                            className="text-gray-600 hover:text-red-400 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  {pollOptions.length < 6 && (
                    <button
                      type="button"
                      onClick={() => setPollOptions([...pollOptions, { text: "" }])}
                      className="mt-2 text-sm text-violet-400 hover:text-violet-300 transition-colors flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add option
                    </button>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm text-gray-400 mb-1.5">
                  {form.post_type === "question" ? "Details (optional)" : "Body (optional)"}
                </label>
                <textarea
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  rows={3}
                  placeholder={form.post_type === "question" ? "Add more context..." : "Details..."}
                  className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Category</label>
                  <CategorySelect
                    value={form.category}
                    onChange={(value) => setForm({ ...form, category: value })}
                    placeholderLabel="Select category"
                    emptyOptionLabel="Choose category"
                    className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                    grouped
                  />
                </div>
                {form.post_type === "announcement" && (
                  <div>
                    <ChipSection
                      type="tag"
                      label="Tags"
                      icon="🏷️"
                      options={filterOptions.tags}
                      selected={form.tags}
                      onToggle={(value) => {
                        const next = form.tags.includes(value)
                          ? form.tags.filter((tag) => tag !== value)
                          : [...form.tags, value];
                        setForm({ ...form, tags: next });
                      }}
                    />
                  </div>
                )}
              </div>

              {form.post_type === "announcement" && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Announcement media upload</label>
                  <input
                    type="file"
                    accept="image/*,video/mp4,video/webm"
                    onChange={async (e) => {
                      const file = e.target.files?.[0] ?? null;
                      setSelectedMediaFile(file);
                      setUploadedMediaUrl(null);
                      if (!file) return;
                      setUploadingMedia(true);
                      try {
                        const result = await adminService.uploadCommunityMedia(file);
                        setUploadedMediaUrl(result.url);
                        setForm((current) => ({ ...current, media_urls: [result.url] }));
                      } catch (uploadErr: any) {
                        setFormError(uploadErr?.response?.data?.detail || "Could not upload media.");
                        setSelectedMediaFile(null);
                      } finally {
                        setUploadingMedia(false);
                      }
                    }}
                    className="w-full text-sm text-gray-300 file:bg-violet-600 file:text-white file:px-4 file:py-2 file:rounded-xl file:border-0 file:mr-3 bg-gray-900 border border-gray-800 rounded-xl"
                  />
                  {uploadingMedia && <p className="text-xs text-gray-400 mt-2">Uploading media…</p>}
                  {uploadedMediaUrl && (
                    <p className="text-xs text-gray-300 mt-2 break-all">Uploaded: <a href={uploadedMediaUrl} target="_blank" rel="noreferrer" className="text-violet-300 underline">Preview file</a></p>
                  )}
                  {!uploadedMediaUrl && !uploadingMedia && (
                    <p className="text-xs text-gray-500 mt-1">Optional announcement image or video upload. Upload is required only for media-enabled announcements.</p>
                  )}
                </div>
              )}

              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1.5">Target Roles</label>
                    <div className="flex flex-wrap gap-2">
                      {(["buyer", "seller"] as const).map((role) => (
                        <button
                          key={role}
                          type="button"
                          onClick={() => {
                            const nextRoles = form.target_roles.includes(role)
                              ? form.target_roles.filter((r) => r !== role)
                              : [...form.target_roles, role];
                            setForm({ ...form, target_roles: nextRoles });
                          }}
                          className={`px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                            form.target_roles.includes(role)
                              ? "bg-violet-600 text-white"
                              : "bg-gray-800 text-gray-400 hover:bg-gray-700"
                          }`}
                        >
                          {role}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Leave blank to send to all users.</p>
                  </div>
                  <div>
                    <ChipSection
                      type="country"
                      label="Target Countries"
                      icon="🌍"
                      options={Array.from(new Set([...geoCountries.map((c) => c.name), ...form.target_countries]))}
                      selected={form.target_countries}
                      onToggle={(value) => {
                        const next = form.target_countries.includes(value)
                          ? form.target_countries.filter((country) => country !== value)
                          : [...form.target_countries, value];
                        setForm({ ...form, target_countries: next });
                      }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <ChipSection
                      type="city"
                      label="Target Cities"
                      icon="🏙️"
                      options={Array.from(new Set([...cityOptions, ...form.target_cities]))}
                      selected={form.target_cities}
                      onToggle={(value) => {
                        const next = form.target_cities.includes(value)
                          ? form.target_cities.filter((city) => city !== value)
                          : [...form.target_cities, value];
                        setForm({ ...form, target_cities: next });
                      }}
                    />
                  </div>
                  <div>
                    <ChipSection
                      type="tag"
                      label="Target Tags"
                      icon="🏷️"
                      options={filterOptions.tags}
                      selected={form.target_tags}
                      onToggle={(value) => {
                        const next = form.target_tags.includes(value)
                          ? form.target_tags.filter((tag) => tag !== value)
                          : [...form.target_tags, value];
                        setForm({ ...form, target_tags: next });
                      }}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="flex items-center gap-2 text-sm text-gray-300">
                      <input
                        type="checkbox"
                        checked={form.mandatory_response}
                        onChange={(e) => setForm({ ...form, mandatory_response: e.target.checked })}
                        className="h-4 w-4 rounded border-gray-600 bg-gray-800 text-violet-500 focus:ring-violet-500"
                      />
                      Mandatory response
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-300">
                      <input
                        type="checkbox"
                        checked={form.send_fcm}
                        onChange={(e) => setForm({ ...form, send_fcm: e.target.checked })}
                        className="h-4 w-4 rounded border-gray-600 bg-gray-800 text-violet-500 focus:ring-violet-500"
                      />
                      Offer FCM push follow-up
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={handleAudiencePreview}
                    disabled={audienceLoading}
                    className="inline-flex items-center justify-center rounded-xl bg-gray-800 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700 disabled:opacity-50 transition-colors"
                  >
                    {audienceLoading ? "Checking..." : "Preview audience"}
                  </button>
                </div>
                {audiencePreview !== null && (
                  <div className="rounded-xl border border-gray-800 bg-gray-900/80 p-4 text-sm text-gray-300 space-y-3">
                    <p className="text-sm text-gray-200">
                      Estimated target audience: <span className="font-semibold text-white">{audiencePreview.total_users}</span> users
                      {audiencePreview.is_global && <span className="ml-2 text-xs text-amber-300">(global — all users)</span>}
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="rounded-lg bg-gray-800/60 border border-gray-800 p-2.5">
                        <p className="text-[11px] uppercase tracking-wide text-gray-500">Buyers</p>
                        <p className="text-base font-semibold text-sky-300">{audiencePreview.buyers}</p>
                      </div>
                      <div className="rounded-lg bg-gray-800/60 border border-gray-800 p-2.5">
                        <p className="text-[11px] uppercase tracking-wide text-gray-500">Sellers</p>
                        <p className="text-base font-semibold text-emerald-300">{audiencePreview.sellers}</p>
                      </div>
                      <div className="rounded-lg bg-gray-800/60 border border-gray-800 p-2.5">
                        <p className="text-[11px] uppercase tracking-wide text-gray-500">Reachable by push</p>
                        <p className="text-base font-semibold text-violet-300">{audiencePreview.users_with_push}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-gray-500">Target roles</p>
                        <p className="text-sm text-white">{form.target_roles.length > 0 ? form.target_roles.join(", ") : "All users"}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-gray-500">Target countries</p>
                        <p className="text-sm text-white">{form.target_countries.length > 0 ? form.target_countries.join(", ") : "Any country"}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-gray-500">Target cities</p>
                        <p className="text-sm text-white">{form.target_cities.length > 0 ? form.target_cities.join(", ") : "Any city"}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-gray-500">Target tags</p>
                        <p className="text-sm text-white">{form.target_tags.length > 0 ? form.target_tags.join(", ") : "Any tag"}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              </div>
              <aside className="w-full lg:w-96 lg:flex-shrink-0">
                <div className="lg:sticky lg:top-2 space-y-4">
                <div className="rounded-3xl border border-gray-800 bg-gray-950/90 p-4 space-y-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500">Live preview</p>
                      <p className="text-sm font-semibold text-white">How this post will look</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center rounded-full border border-gray-700 bg-gray-900 px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-gray-400">
                        {form.post_type === "poll" ? "Poll" : form.post_type === "question" ? "Q&A" : "Announcement"}
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowPreviewModal(true)}
                        className="inline-flex items-center rounded-xl bg-violet-600 px-3 py-2 text-xs font-medium text-white hover:bg-violet-500 transition-colors"
                      >
                        Open preview
                      </button>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-wide text-gray-500">Title</p>
                      <p className="text-lg font-semibold text-white">{form.title || "Post title preview"}</p>
                    </div>
                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-wide text-gray-500">Body</p>
                      <p className="whitespace-pre-wrap text-sm text-gray-300 min-h-[3rem]">{form.body || "Post body preview..."}</p>
                    </div>
                    {previewMediaUrl && (
                      <div className="rounded-2xl border border-gray-800 overflow-hidden bg-black">
                        {isPreviewImage ? (
                          <img src={previewMediaUrl} alt="Preview media" className="w-full object-cover" />
                        ) : isPreviewVideo ? (
                          <video src={previewMediaUrl} controls className="w-full" />
                        ) : (
                          <a href={previewMediaUrl} target="_blank" rel="noreferrer" className="block p-3 text-sm text-violet-300 underline">Preview media link</a>
                        )}
                      </div>
                    )}
                    <div className="grid gap-2 sm:grid-cols-2">
                      <div className="rounded-2xl border border-gray-800 bg-gray-900 p-3">
                        <p className="text-[11px] uppercase tracking-wide text-gray-500">Category</p>
                        <p className="mt-1 text-sm text-white">{form.category || "No category selected"}</p>
                      </div>
                      <div className="rounded-2xl border border-gray-800 bg-gray-900 p-3">
                        <p className="text-[11px] uppercase tracking-wide text-gray-500">Tags</p>
                        <p className="mt-1 text-sm text-white">{form.tags.length > 0 ? form.tags.join(", ") : "No tags"}</p>
                      </div>
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <div className="rounded-2xl border border-gray-800 bg-gray-900 p-3">
                        <p className="text-[11px] uppercase tracking-wide text-gray-500">Target roles</p>
                        <p className="mt-1 text-sm text-white">{form.target_roles.length > 0 ? form.target_roles.join(", ") : "All users"}</p>
                      </div>
                      <div className="rounded-2xl border border-gray-800 bg-gray-900 p-3">
                        <p className="text-[11px] uppercase tracking-wide text-gray-500">Mandatory response</p>
                        <p className="mt-1 text-sm text-white">{form.mandatory_response ? "Yes" : "No"}</p>
                      </div>
                    </div>
                  </div>
                </div>
                </div>
              </aside>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditingId(null); }}
                  className="flex-1 px-4 py-2.5 bg-gray-800 text-gray-300 rounded-xl text-sm font-medium hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2.5 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white rounded-xl text-sm font-medium transition-colors"
                >
                  {submitting ? "Saving..." : editingId ? "Save changes" : form.post_type === "poll" ? "Launch Poll" : "Publish"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPreviewModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-end bg-black/40 p-4 backdrop-blur-sm">
          <div className="w-full max-w-2xl h-full overflow-y-auto rounded-3xl border border-gray-800 bg-gray-950 shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-800 p-6">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Preview modal</p>
                <h3 className="text-lg font-semibold text-white">Post preview</h3>
              </div>
              <button onClick={() => setShowPreviewModal(false)} className="text-gray-400 hover:text-white transition-colors">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-wide text-gray-500">Title</p>
                <p className="text-lg font-semibold text-white">{form.title || "Post title preview"}</p>
              </div>
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-wide text-gray-500">Body</p>
                <p className="whitespace-pre-wrap text-sm text-gray-300 min-h-[3rem]">{form.body || "Post body preview..."}</p>
              </div>
              {previewMediaUrl && (
                <div className="rounded-2xl border border-gray-800 overflow-hidden bg-black">
                  {isPreviewImage ? (
                    <img src={previewMediaUrl} alt="Preview media" className="w-full object-cover" />
                  ) : isPreviewVideo ? (
                    <video src={previewMediaUrl} controls className="w-full" />
                  ) : (
                    <a href={previewMediaUrl} target="_blank" rel="noreferrer" className="block p-3 text-sm text-violet-300 underline">Preview media link</a>
                  )}
                </div>
              )}
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="rounded-2xl border border-gray-800 bg-gray-900 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500">Category</p>
                  <p className="mt-1 text-sm text-white">{form.category || "No category selected"}</p>
                </div>
                <div className="rounded-2xl border border-gray-800 bg-gray-900 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500">Tags</p>
                  <p className="mt-1 text-sm text-white">{form.tags.length > 0 ? form.tags.join(", ") : "No tags"}</p>
                </div>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="rounded-2xl border border-gray-800 bg-gray-900 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500">Target roles</p>
                  <p className="mt-1 text-sm text-white">{form.target_roles.length > 0 ? form.target_roles.join(", ") : "All users"}</p>
                </div>
                <div className="rounded-2xl border border-gray-800 bg-gray-900 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-gray-500">Mandatory response</p>
                  <p className="mt-1 text-sm text-white">{form.mandatory_response ? "Yes" : "No"}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Delete Post"
          description={`"${deleteTarget.title?.slice(0, 60) || "This post"}" and all its comments will be removed.`}
          items={["Delete post from database", "Delete all comments and reactions"]}
          confirmLabel="Delete Post"
          busy={deleting}
          busyLabel="Deleting…"
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
      {alertModal}
    </>
  );
};

export default AdminPollsPage;
